/**
 * Dispatch redesign — frontend selectors (the contracts the HANDOFF asks to
 * "define up front", see `.design-ref/dispatch/HANDOFF.md`).
 *
 * These centralize the derived state the redesign UI needs so components read
 * one source instead of re-deriving from raw fields. Where the data isn't in
 * the current API response shape (flagged by the Codex pre-handoff review),
 * the selector returns an honest empty/partial result and is annotated — that
 * is the seam where a Phase-2 query change plugs in.
 */
import { type Task, type Trip, TripStatus } from "@/api.types.v2";

type InstructionTask = {
  type: string;
  shipment?: { deliveryNotes?: string | null } | null;
  addressInstructions?: string | null;
};

/** Current delivery instructions; pickup address guidance remains separate. */
export function taskInstructions(task: InstructionTask): string | null {
  return task.type === "CUSTOMER_DELIVERY"
    ? task.shipment?.deliveryNotes?.trim() || null
    : task.addressInstructions?.trim() || null;
}

export function taskInstructionLabel(
  task: Pick<InstructionTask, "type">
): string {
  return task.type === "CUSTOMER_DELIVERY" ? "Delivery instructions" : "Access";
}

// ── Readiness gate ─────────────────────────────────────────────────────────
// A trip is "receivable" by the operator app when it has work, a driver, and
// is still in the planning state. All three signals already exist on Trip.
export interface TripReadiness {
  hasWaypoints: boolean;
  hasOperator: boolean;
  isPlanned: boolean;
  /** All three true → the operator app will surface it. */
  receivable: boolean;
}

// `waypointCount` override: some screens (e.g. the trips list) load waypoints in
// a separate query rather than on `trip.waypoints`. Pass the count when you have
// it so readiness is accurate; otherwise we fall back to `trip.waypoints`.
export function isReceivableTrip(
  trip: Trip,
  waypointCount?: number
): TripReadiness {
  const hasWaypoints = (waypointCount ?? trip.waypoints?.length ?? 0) > 0;
  const hasOperator = Boolean(trip.operatorId);
  const isPlanned = trip.status === TripStatus.PLANNED;
  return {
    hasWaypoints,
    hasOperator,
    isPlanned,
    receivable: hasWaypoints && hasOperator && isPlanned,
  };
}

// ── Care signals ───────────────────────────────────────────────────────────
// "Must-know" signals for a stop: free-text notes + handling flags. Note and
// fragile are typed and derivable today. Add-on services (signature/insurance/
// packaging/special-handling) are NOT a field on Task/Shipment in the current
// response — left empty until the task query includes them (Codex caveat).
// Care-service taxonomy mirrors the design's SERVICE_ICON set. Today only
// `fragile` (ShipmentPackage.isFragile) and `cold` (requiresCooling) are
// derivable from the live response; signature/cod/appointment/heavy need an
// add-on source the dispatch query doesn't return yet (Codex caveat) — the UI
// renders them whenever they appear.
export type CareService =
  | "signature"
  | "fragile"
  | "cod"
  | "appointment"
  | "heavy"
  | "cold";

export interface TaskCareSignals {
  note: string | null;
  fragile: boolean;
  services: CareService[];
  /** True if the marker/row should show any care affordance at all. */
  hasAny: boolean;
}

export function getTaskCareSignals(task: Task): TaskCareSignals {
  const note = task.shipment?.deliveryNotes?.trim() || null;
  const packages = task.shipment?.packages ?? [];
  const fragile = packages.some((p) => p.isFragile);
  const cold = packages.some((p) => p.requiresCooling);
  const services: CareService[] = [];
  if (fragile) services.push("fragile");
  if (cold) services.push("cold");
  return {
    note,
    fragile,
    services,
    hasAny: Boolean(note) || services.length > 0,
  };
}

// ── Capacity summary ───────────────────────────────────────────────────────
// Vehicle.capacity / currentLoad are untyped records today (Codex caveat), so
// a true load/limit bar isn't computable. We expose what IS reliable — stop and
// task counts — plus the raw vehicle records for whoever can interpret them.
export interface TripCapacitySummary {
  stops: number;
  tasks: number;
  /** Untyped vehicle records, passed through; null when no vehicle. */
  vehicleCapacity: Record<string, unknown> | null;
  vehicleLoad: Record<string, unknown> | null;
  /** False today — no numeric load/limit available. */
  capacityKnown: boolean;
}

export function getTripCapacitySummary(trip: Trip): TripCapacitySummary {
  const waypoints = trip.waypoints ?? [];
  const tasks = waypoints.reduce((n, wp) => n + (wp.tasks?.length ?? 0), 0);
  return {
    stops: waypoints.length,
    tasks,
    vehicleCapacity: trip.vehicle?.capacity ?? null,
    vehicleLoad: trip.vehicle?.currentLoad ?? null,
    capacityKnown: false,
  };
}
